// services/media.service.js

// import api from '@/api'
import { instance } from '@/api'
import { isFilePrototype } from '@/utils/files'

const cache = new Map()

/**
 * Возвращает путь для загрузки файла.
 * Поддерживает как строковый URL, так и объект файла.
 */
function getPath (file) {
  if (typeof file === 'string') {
    return normalizePath(file)
  }

  return normalizePath(file?.url || file?.original_url || `/api/media/file/${file.id}`)
}

/**
 * Убирает дублирование `/api` в URL,
 * если baseURL уже содержит этот префикс.
 */
function normalizePath (path) {
  if (!path) return ''

  // Если baseURL уже заканчивается на /api,
  // а путь начинается с /api — убираем дублирование.
  if (
    process.env.VUE_APP_API_URL.endsWith('/api') ||
    process.env.VUE_APP_API_URL.endsWith('/api/')
  ) {
    return path.replace(/^\/api/, '')
  }

  return path
}

/**
 * Получает имя файла из заголовка Content-Disposition.
 * Если заголовок отсутствует, возвращает fallback.
 */
function getFileNameFromHeaders (headers = {}, fallback = 'file') {
  const cd = headers['content-disposition'] || ''

  // filename*=utf-8''...
  let match = /filename\*=utf-8''([^;]+)/i.exec(cd)

  if (match) {
    return decodeURIComponent(match[1])
  }

  // filename="..."
  match = /filename="?([^"]+)"?/i.exec(cd)

  if (match) {
    return match[1]
  }

  return fallback
}

/**
 * Загружает файл с сервера без использования кэша.
 * Возвращает Blob и заголовки ответа.
 */
async function fetchFile (path) {
  const response = await instance.get(path, {
    responseType: 'blob'
  })

  return {
    blob: response.data,
    headers: response.headers
  }
}

/**
 * Загружает файл с сервера, создаёт Object URL
 * и сохраняет результат в кэш.
 */
async function loadFile (path) {
  const media = await fetchFile(path)

  media.objectUrl = URL.createObjectURL(media.blob)

  cache.set(path, media)

  return media
}

/**
 * Возвращает Blob файла.
 * Если файл уже есть в кэше — используется кэш,
 * иначе выполняется загрузка.
 */
async function getBlob (file) {
  const media = await load(file)
  return media?.blob || null
}

/**
 * Возвращает Object URL файла.
 * Использует кэш либо загружает файл при необходимости.
 */
async function getObjectUrl (file) {
  const media = await load(file)
  return media?.objectUrl || ''
}

/**
 * Основная функция загрузки файла.
 *
 * - Для локальных File создаёт Object URL и кэширует его.
 * - Для файлов с сервера загружает Blob, создаёт Object URL
 *   и сохраняет результат в кэш.
 * - Если файл уже загружался ранее — возвращает данные из кэша.
 */
async function load (originalFile) {
  if (!originalFile) {
    return null
  }
  const file = originalFile.pdf || originalFile

  if (isFilePrototype(file)) {
    if (cache.has(file)) {
      return cache.get(file)
    }

    const media = {
      blob: file,
      objectUrl: URL.createObjectURL(file),
      headers: {}
    }

    cache.set(file, media)

    return media
  }

  const path = getPath(file)
  return cache.get(path) || await loadFile(path)
}

/**
 * Освобождает Object URL и удаляет файл из кэша.
 */
function revoke (file) {
  const path = getPath(file)
  const cached = cache.get(path)
  if (!cached) {
    return
  }

  URL.revokeObjectURL(cached.objectUrl)
  cache.delete(path)
}

/**
 * Полностью очищает кэш файлов
 * и освобождает все Object URL.
 */
function clearCache () {
  cache.forEach(url => URL.revokeObjectURL(url))
  cache.clear()
}

/**
 * Открывает файл в новой вкладке браузера.
 * При необходимости предварительно загружает его.
 */
async function open (file) {
  const objectUrl = await getObjectUrl(file)

  window.open(objectUrl, '_blank')
}

/**
 * Скачивает файл.
 */
async function download (file) {
  let media

  if (isFilePrototype(file)) {
    media = {
      blob: file,
      headers: {}
    }
  } else {
    const path = getPath(file)

    media = cache.get(path)

    if (!media) {
      media = await fetchFile(path)
    }
  }

  const url = URL.createObjectURL(media.blob)

  const a = document.createElement('a')
  a.href = url
  a.download = getFileNameFromHeaders(
    media.headers,
    file?.file_name || file?.name || 'file'
  )

  document.body.appendChild(a)
  a.click()
  a.remove()

  URL.revokeObjectURL(url)
}

/**
 * Создаёт объект File из ответа axios.
 * Используется для эндпоинтов, которые сразу возвращают Blob.
 *
 * Дополнительно устанавливает признак downloadable,
 * чтобы отличать такие файлы от локально выбранных пользователем.
 */
function createFileFromResponse (response, fallbackName = 'report') {
  const file = new File(
    [response.data],
    getFileNameFromHeaders(response.headers, fallbackName),
    { type: response.data.type }
  )

  file.downloadable = true

  return file
}

export default {
  getBlob,
  getObjectUrl,
  load,
  open,
  download,
  revoke,
  clearCache,
  getFileNameFromHeaders,
  createFileFromResponse
}
